import { useEffect, useMemo, useState } from 'react';
import {
  ApiClientError,
  type ApiClientErrorCode,
  type DecorFacetsResponse,
  type DecorKind,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';

/**
 * Опции фасета каталога декора ИЗ ДАННЫХ (ADR-0054/0059).
 *
 * Тянет `GET /api/decors/:kind/facets`. Скоуп: Бренд — под выбранные подкатегории
 * (`selectedSubs`); Тип — под подкатегории И выбранные бренды (`selectedBrands`,
 * каскад Бренд→Тип). Подкатегории в ответе — из ПОЛНОГО пула kind (стабильны).
 * Перезапрос при смене kind / набора подкатегорий / набора брендов.
 *
 * `brandOptions`/`typeOptions` — стабильные ссылки (массивы из ответа), важно для
 * эффекта pruning у контейнера. `subcategoryOptions` мемоизируется.
 */
export interface DecorFacetsState {
  subcategoryOptions: readonly string[];
  brandOptions: readonly string[];
  typeOptions: readonly string[];
  loading: boolean;
  error?: { code: ApiClientErrorCode; message: string };
}

const EMPTY_FACETS: DecorFacetsResponse = { subcategories: [], brands: [], types: [] };

export function useDecorFacets(
  kind: DecorKind,
  selectedSubs: readonly string[],
  selectedBrands: readonly string[] = [],
): DecorFacetsState {
  const [facets, setFacets] = useState<DecorFacetsResponse>(EMPTY_FACETS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  // Стабильные ключи наборов для deps (порядок не важен для скоупа).
  const subsKey = useMemo(() => [...selectedSubs].slice().sort().join('|'), [selectedSubs]);
  const brandsKey = useMemo(() => [...selectedBrands].slice().sort().join('|'), [selectedBrands]);

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    let cancelled = false;
    const subs = subsKey.length > 0 ? subsKey.split('|') : undefined;
    const brands = brandsKey.length > 0 ? brandsKey.split('|') : undefined;
    apiClient.decors.facets(kind, subs, brands).then(
      (res) => {
        if (cancelled) return;
        setFacets(res);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        // Не leak-аем String(err) в UI (security): non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setFacets(EMPTY_FACETS);
        setError(apiErr);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [kind, subsKey, brandsKey]);

  const subcategoryOptions = useMemo(
    () => facets.subcategories.map((s) => s.value),
    [facets.subcategories],
  );

  return {
    subcategoryOptions,
    brandOptions: facets.brands,
    typeOptions: facets.types,
    loading,
    error,
  };
}
